/**
 * ppd-design-cycle — hover-to-preview cycler for Browse by Design cards.
 *
 * It drives any subtree matching:
 *   [data-ppd-design-cycle]   the card root (only rendered when a design
 *                             has more than one product photo)
 *     [data-ppd-design-frame] one frame per product (.is-active shows it);
 *     [data-ppd-design-dot]   one button per frame; selecting a frame shows
 *                             its preview
 *
 * Behaviour:
 *   - at rest the card carries .is-idle and the visible photo drifts
 *     (a slow zoom owned by the section stylesheet); cycling starts on
 *     pointerenter/focusin — which drops .is-idle — and stops on
 *     pointerleave/focusout, resetting to the first product and idle drift
 *   - the card link always opens the design collection, where visitors can
 *     choose from every piece shown by its count
 *   - dots are real buttons outside the anchor: keyboard and touch visitors
 *     can preview a frame without hovering
 *   - honours prefers-reduced-motion (no autoplay; dots still work)
 */
(() => {
  const MOUNT_SELECTOR = '[data-ppd-design-cycle]';
  const INTERVAL_MS = 1800;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function show(root, index) {
    const frames = Array.from(root.querySelectorAll('[data-ppd-design-frame]'));
    const dots = Array.from(root.querySelectorAll('[data-ppd-design-dot]'));
    if (!frames.length) return;
    const current = ((index % frames.length) + frames.length) % frames.length;
    frames.forEach((frame, i) => {
      frame.classList.toggle('is-active', i === current);
    });
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === current);
      dot.setAttribute('aria-pressed', i === current ? 'true' : 'false');
    });
    root.__ppdDesignIndex = current;
  }

  function stop(root) {
    if (root.__ppdDesignTimer) {
      window.clearInterval(root.__ppdDesignTimer);
      root.__ppdDesignTimer = null;
    }
  }

  function start(root) {
    const frames = root.querySelectorAll('[data-ppd-design-frame]');
    if (frames.length < 2 || reduceMotion.matches) return;
    root.classList.remove('is-idle');
    stop(root);
    root.__ppdDesignTimer = window.setInterval(() => {
      show(root, (root.__ppdDesignIndex || 0) + 1);
    }, INTERVAL_MS);
  }

  function reset(root) {
    stop(root);
    show(root, 0);
    if (!reduceMotion.matches) root.classList.add('is-idle');
  }

  function init(root) {
    if (root.__ppdDesignReady) return;
    root.__ppdDesignReady = true;
    root.__ppdDesignIndex = 0;
    root.classList.add('is-cycling');
    if (!reduceMotion.matches) root.classList.add('is-idle');

    root.addEventListener('pointerenter', () => start(root));
    root.addEventListener('pointerleave', () => reset(root));
    root.addEventListener('focusin', () => start(root));
    root.addEventListener('focusout', () => reset(root));

    const dots = Array.from(root.querySelectorAll('[data-ppd-design-dot]'));
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        show(root, i);
        if (root.__ppdDesignTimer) start(root);
      });
    });
  }

  function mount() {
    document.querySelectorAll(MOUNT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
